import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { MessagingChannelMemberRole } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { MouseEvent, useState } from 'react';

import { MessagingChannel } from '~hooks/useMessaging/models';
import { useStores } from '~store';

/** Roughly eight rows, so a 500-recipient broadcast scrolls inside the popover. */
const MEMBER_LIST_MAX_HEIGHT_IN_PX = 360;
const MEMBER_LIST_WIDTH_IN_PX = 320;

type MessagingChannelMember = MessagingChannel['members'][number];

type MessagingChannelMembersButtonProps = {
  channel: MessagingChannel;
  label: string;
};

const byDisplayName = (a: MessagingChannelMember, b: MessagingChannelMember) =>
  (a.displayName ?? '').localeCompare(b.displayName ?? '');

type MessagingChannelMemberListProps = {
  channel: MessagingChannel;
};

const MessagingChannelMemberList = observer(
  ({ channel }: MessagingChannelMemberListProps) => {
    const theme = useTheme();
    const { userStore } = useStores();

    const viewerUserId = userStore.user?.id;
    // Members are listed for the session, so compare against the viewer's own company, not the selected account.
    const viewerCompanyId = userStore.userCompany.id;

    const owners = channel.members.filter(
      (member) => member.role === MessagingChannelMemberRole.OWNER,
    );
    const members = [...owners, ...[...channel.recipients].sort(byDisplayName)];
    const viewerMember = channel.memberFor(viewerUserId);

    return (
      <Box sx={{ width: MEMBER_LIST_WIDTH_IN_PX }}>
        <Typography
          sx={{
            borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
            px: 2,
            py: 1,
          }}
          variant="subtitle2"
        >
          {t('messaging.members.heading', { count: members.length })}
        </Typography>

        <Box
          component="ul"
          data-test-id="messaging-channel-members-list"
          sx={{
            listStyle: 'none',
            m: 0,
            maxHeight: MEMBER_LIST_MAX_HEIGHT_IN_PX,
            overflowY: 'auto',
            px: 0,
            py: 0.5,
          }}
        >
          {members.map((member) => {
            const isOrganizer = member.role === MessagingChannelMemberRole.OWNER;
            const isViewer = member.id === viewerMember?.id;
            const bothCompaniesAreKnown =
              Boolean(viewerCompanyId) && Boolean(member.companyId);
            const isFromAnotherCompany =
              bothCompaniesAreKnown && member.companyId !== viewerCompanyId;

            return (
              <Box component="li" key={member.id} sx={{ px: 2, py: 0.75 }}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                  <Typography noWrap sx={{ minWidth: 0 }} variant="body2">
                    {member.displayName || t('messaging.members.unknown_member')}
                  </Typography>
                  {isOrganizer && (
                    <Chip label={t('messaging.members.organizer')} size="small" />
                  )}
                  {isViewer && (
                    <Chip
                      label={t('messaging.members.you')}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                {isFromAnotherCompany && member.companyName && (
                  <Typography
                    noWrap
                    sx={{ color: theme.brandV2.colors.treadGray3, display: 'block' }}
                    variant="caption"
                  >
                    {member.companyName}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  },
);

export default function MessagingChannelMembersButton({
  channel,
  label,
}: MessagingChannelMembersButtonProps) {
  const theme = useTheme();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const isOpen = Boolean(anchorElement);

  const onOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const onClose = () => {
    setAnchorElement(null);
  };

  return (
    <>
      <ButtonBase
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('messaging.members.open', { subtitle: label })}
        data-test-id="messaging-channel-members-button"
        onClick={onOpen}
        sx={{
          alignItems: 'center',
          borderRadius: theme.brandV2.borderRadius,
          color: theme.brandV2.colors.treadGray3,
          gap: 0.25,
          maxWidth: '100%',
          '&:hover': { color: theme.brandV2.colors.treadBlack },
        }}
      >
        <Typography noWrap sx={{ color: 'inherit' }} variant="caption">
          {label}
        </Typography>
        <ExpandMoreIcon fontSize="inherit" />
      </ButtonBase>

      <Popover
        anchorEl={anchorElement}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={onClose}
        open={isOpen}
      >
        <MessagingChannelMemberList channel={channel} />
      </Popover>
    </>
  );
}
